import _ from 'lodash'
import type { Store } from 'pinia'
import { ObservationCategory } from '~/components/chart/types'
import type { DeviceConstant, UIObservation, UIObservationDefinition } from '~/repository/types/UIObservation'
import type { DeviceDefinition } from '~/repository/types/directus/device/deviceDefinition'
import {
  RepresentationTypeEnum,
  type ObservationDefinition,
  BLOOD_PRESSURE_CODE,
  type ObservationDomain,
} from '~/repository/types/directus/observationDefinition'
import type { ObservationCount } from '~/repository/types/observation'

export const parseObservationDefinitionData = (observationsDefinitionData: ObservationDefinition[]) => {
  //Create a generic bloodPressureDef
  let bloodPressureDef: ObservationDefinition | undefined = _.find(observationsDefinitionData, (obs) => {
    return obs.representation == RepresentationTypeEnum.bloodPressure
  })
  bloodPressureDef = _.cloneDeep(bloodPressureDef)
  bloodPressureDef!.code = BLOOD_PRESSURE_CODE
  bloodPressureDef!.translations[0].short_name = 'PA'
  bloodPressureDef!.translations[0].name = 'Pression Artérielle'
  observationsDefinitionData.push(bloodPressureDef)

  const observationsDefinition: { [observationCode: string]: ObservationDefinition } = {}
  observationsDefinitionData.forEach((obs: ObservationDefinition) => {
    observationsDefinition[obs.code] = obs
  })

  return observationsDefinition
}

export const parseFilterSets = (
  filterSetData: { observations: []; translations: [] }[],
  codeList: { code: string; count: number }[],
  observationsDefinition: { [observationCode: string]: UIObservationDefinition },
) => {
  const availableConstants = _.flatMap(codeList, (d) => d.code)
  const filterSetList: { title: string; value: string[] }[] = []
  filterSetData.forEach((data) => {
    let filterData = _.flatMap(data.observations, (d) => d.observations_code)

    let intersection = filterData.length
      ? availableConstants.filter((element) => filterData.includes(element))
      : availableConstants

    //keep code with definition
    intersection = _.filter(intersection, (i) => observationsDefinition[i])

    filterSetList.push({
      title: data.translations[0].name,
      value: intersection.toString(),
      props: { disabled: intersection.toString().length == 0 },
    })
  })

  return filterSetList
}

export const getDeviceByConstant = (devicesConstant: DeviceConstant[], constants: string[]) => {
  let deviceList: string[] = []
  constants.forEach((constant) => {
    devicesConstant.forEach((deviceConstant) => {
      if (deviceConstant.constants.indexOf(constant)) {
        deviceList.push(deviceConstant.deviceId)
      }
    })
  })
  return _.uniq(deviceList)
}
export const parseConstantListDataForDevice = (codeList: ObservationCount[]) => {
  const devicesList: { title: string; value: DeviceConstant }[] = []
  const deviceStore = useDevicesStore()
  const observationsDefinition = useObservationDefinitionsStore()
  const codesWithDevices: ObservationCount[] = _.filter(codeList, (data) => {
    return data.devices.length
  }) as ObservationCount[]

  const deviceDico: {
    [definitionCode: string]: string[]
  } = {}
  codesWithDevices.forEach((codeData: ObservationCount) => {
    codeData.devices.forEach((deviceDefinition) => {
      if (!deviceDico[deviceDefinition.definition]) deviceDico[deviceDefinition.definition] = []
      if (observationsDefinition.getByCode(codeData.code)) {
        deviceDico[deviceDefinition.definition].push(codeData.code)
      }
    })
  })

  Object.keys(deviceDico).forEach((deviceDefinitionId) => {
    if (deviceDico[deviceDefinitionId].length) {
      devicesList.push({
        title: `${deviceStore.getDeviceById(deviceDefinitionId)?.translations[0].name}`,
        value: { deviceId: deviceDefinitionId, constants: _.uniq(deviceDico[deviceDefinitionId]) },
      })
    }
  })

  return devicesList
}
export const parseConstantListData = (
  codeList: { code: string; count: number }[],
  observationsDefinition: { [observationCode: string]: UIObservationDefinition },
) => {
  //Create a generic bloodPressureDef
  const constantsList: { title: string; value: string[]; count: number; category: ObservationCategory; color: string }[] = []
  let bloodPressureCount: number = 0
  codeList.forEach((c) => {
    if (c.code.indexOf(BLOOD_PRESSURE_CODE) != -1) {
      bloodPressureCount += c.count
      let bloodPressureEntry = _.find(constantsList, (entry) => entry.title.indexOf('Pression artérielle') != -1)
      if (!bloodPressureEntry) {
        bloodPressureEntry = {
          title: `Pression artérielle`,
          value: [c.code],
          count: bloodPressureCount,
          color: observationsDefinition[c.code].color,
          category: ObservationCategory.VITAL_SIGNS,
        }
        constantsList.push(bloodPressureEntry)
      } else {
        bloodPressureEntry.title = `Pression artérielle`
        bloodPressureEntry.value.push(c.code)
        bloodPressureEntry.count = bloodPressureCount
        bloodPressureEntry.color = observationsDefinition[c.code].color
        bloodPressureEntry.category = ObservationCategory.VITAL_SIGNS
      }
    } else {
      if (observationsDefinition[c.code]) {
        constantsList.push({
          title: observationsDefinition[c.code] ? `${observationsDefinition[c.code]!.translations[0].name}` : `(${c.code})`,
          value: [c.code],
          count: c.count,
          color: observationsDefinition[c.code].color,
          category: observationsDefinition[c.code]
            ? (observationsDefinition[c.code]!.category as ObservationCategory)
            : ObservationCategory.VITAL_SIGNS,
        })
      } else {
        console.log('no definition in directus for :' + c.code)
      }
    }
  })

  return constantsList
}

export const parseRangeSelectorData = (rawRangeSelectorData: any[], domain: ObservationDomain) => {
  let hoursCount: number = Math.ceil((domain.endDate.getTime() - domain.startDate.getTime()) / (1000 * 60 * 60))
  let dayCount: number = Math.ceil((domain.endDate.getTime() - domain.startDate.getTime()) / (1000 * 60 * 60 * 24))
  let isHourSplit: boolean = dayCount <= 7

  let hours: number = 0
  rawRangeSelectorData.forEach((obs) => {
    if (!isHourSplit) {
      obs.simplifiedDate = new Date(obs.date).setHours(0, 0, 0, 0)
    } else {
      hours = parseInt(obs.date.toString().split('T')[1].split(':')[0])
      obs.simplifiedDate = new Date(obs.date).setHours(hours, 0, 0, 0)
    }
  })

  //handle missing days
  let intervalCount: number
  if (!isHourSplit) {
    intervalCount = dayCount
  } else {
    intervalCount = hoursCount > 24 ? dayCount * 24 : hoursCount - 1
  }

  let dateSimplified: number
  let temprawRangeSelectorData = []
  for (let i = 0; i <= intervalCount; i++) {
    let simplifiedStart: Date = new Date(new Date(domain.startDate).setHours(0, 0, 0, 0))
    if (!isHourSplit) {
      dateSimplified = simplifiedStart.setDate(simplifiedStart.getDate() + i)
    } else {
      simplifiedStart = new Date(new Date(domain.startDate).setHours(domain.startDate.getHours(), 0, 0, 0))
      dateSimplified = simplifiedStart.setHours(simplifiedStart.getHours() + i)
    }
    let currentData = rawRangeSelectorData.find((d) => d.simplifiedDate == dateSimplified)
    if (currentData) {
      temprawRangeSelectorData.push({
        value: currentData.count,
        date: new Date(dateSimplified),
        key: i + 1,
      })
    } else {
      temprawRangeSelectorData.push({
        value: 0,
        date: new Date(dateSimplified),
        key: i + 1,
      })
    }
  }
  return temprawRangeSelectorData
}
export const normalizeValue = (value: number, definition: ObservationDefinition) => {
  let normalizedValue: number = (value - definition.range_min) / (definition.range_max - definition.range_min)
  return normalizedValue > 1 ? 1 : normalizedValue < 0 ? 0 : normalizedValue
}

export const getDayString = (date: Date) => {
  return date && !isNaN(date.getTime())
    ? date.toLocaleDateString('fr-FR', { day: 'numeric', weekday: 'long', month: 'long', year: 'numeric' })
    : '--'
}

export const getDayShortString = (date: Date) => {
  return date && !isNaN(date.getTime())
    ? date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric', year: 'numeric' })
    : '--'
}

export const getTimeString = (date: Date) => {
  return date && !isNaN(date.getTime()) ? date.toLocaleString('fr-FR', { hour: 'numeric', minute: 'numeric' }) : '--'
}
